import { Fragment, useMemo } from 'react';

import groupBy from 'lodash.groupby';
import type { UseFormReturn } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FormCheckbox,
  FormField,
  FormInput,
} from '@/_components';
import { CardContainer, FormLayout, SectionHeading } from '@/_containers';
import { permissionsRouteTree, twSort } from '@/_shared';
import {
  dashboardWidgetsPermissionConfig,
  type WidgetModelName,
} from '@/_shared/routes-widgets/configs/permissible-widget-keys';
import type { RouteListItemWithChildren } from '@/types';

import type { UserRoleFormSchema } from './schemas';

const checkboxStyle = twSort`flex flex-col-reverse items-center justify-center gap-3 space-y-0 space-x-0`;

export function FormFields({
  form,
  isEditable = true,
  autoFocus = false,
}: {
  form: UseFormReturn<UserRoleFormSchema>;
  isEditable?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <>
      <CardContainer>
        <FormLayout>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-4"
                label="User Role (required)"
                {...field}
                disabled={!isEditable}
                autoFocus={autoFocus}
              />
            )}
          />
        </FormLayout>
      </CardContainer>
      <CardContainer>
        <SectionHeading text="Permissions" />
        <div className="mt-4 flex flex-col overflow-x-auto">
          {permissionsRouteTree.map((item, index) => {
            return (
              <OneSetOfPermissions
                className={twMerge(
                  'border-c-border border-b pt-2.5 pb-3',
                  index === permissionsRouteTree.length - 1 ? 'border-b-0' : ''
                )}
                key={item.auditKey}
                labelClassName={index === 0 ? '' : 'hidden'}
                {...item}
                form={form}
                isEditable={isEditable}
              />
            );
          })}
        </div>
      </CardContainer>
      <WidgetPermissions form={form} isEditable={isEditable} />
    </>
  );
}

function OneSetOfPermissions({
  auditKey,
  name,
  form,
  sub,
  isEditable = true,
  className,
  labelClassName,
}: RouteListItemWithChildren & {
  form: UseFormReturn<UserRoleFormSchema>;
  isEditable?: boolean;
  className?: string;
  labelClassName: string;
}) {
  const [permissions] = form.watch(['permissions']);

  const { allSubTabsWithPermission, model } = useMemo(() => {
    const model = permissions.filter((item) => item.resource === auditKey);

    if (auditKey === 'dashboard' || !model) {
      return { model: null, allSubTabsWithPermission: [] };
    }
    const tabs = permissions.filter((item) => item.resource.split('/')[0] === auditKey && item.resource !== auditKey);
    const groupedTabs = groupBy(tabs, 'resource');

    const allSubTabsWithPermission = Object.keys(groupedTabs).map((key) => {
      const permissions = groupedTabs[key];
      const keyParts = key.split('/');
      const subKey = keyParts[keyParts.length - 1];
      const subValues = sub.find((item) => item.path.endsWith('/' + subKey));

      return {
        key: auditKey + '/' + subKey,
        permissions,
        name: subValues?.name || '',
      };
    });

    return {
      allSubTabsWithPermission,
      model: groupBy(model, 'resource'),
    };
  }, [auditKey, permissions, sub]);

  if (!model) {
    return null;
  }

  // console.log({ model, allSubTabsWithPermission });

  return (
    <Accordion type="single" collapsible className={twMerge('mt-0 w-full', className)}>
      <AccordionItem value="item-1">
        <section className="flex items-end gap-1 gap-x-2">
          <div className="w-7 shrink-0 grow-0 pb-0.5">
            {allSubTabsWithPermission.length !== 0 && <AccordionTrigger />}
          </div>
          <div className="text-accent w-48 shrink-0 grow-0 text-left text-sm font-semibold dark:text-white">{name}</div>

          {auditKey &&
            model[auditKey].map((item) => {
              return (
                <div key={item.index} className="min-w-12 flex-1">
                  <FormField
                    control={form.control}
                    name={`permissions.${item.index}.value`}
                    render={({ field }) => (
                      <FormCheckbox
                        hideMsg
                        label={item.action}
                        innerClassName={checkboxStyle}
                        className={auditKey === 'settings' && item.action !== 'view' ? 'opacity-50' : ''}
                        labelClassName={twMerge('text-c-card-muted text-sm font-medium capitalize', labelClassName)}
                        checked={!!field.value}
                        disabled={!isEditable || (auditKey === 'settings' && item.action !== 'view')}
                        onChange={(e) => {
                          field.onChange(e.target.checked);

                          if (item.action === 'delete' && e.target.checked) {
                            form.setValue(`permissions.${item.index - 1}.value`, e.target.checked);
                            form.setValue(`permissions.${item.index - 2}.value`, e.target.checked);
                            form.setValue(`permissions.${item.index - 3}.value`, e.target.checked);
                            form.setValue(`permissions.${item.index - 4}.value`, e.target.checked);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              );
            })}
        </section>

        {allSubTabsWithPermission.length !== 0 && (
          <AccordionContent className="mt-3 flex flex-col gap-3">
            {allSubTabsWithPermission.map((gTab) => {
              return (
                <section className="flex gap-2" key={gTab.key}>
                  <div className="w-7 shrink-0 grow-0" />
                  <div className="text-accent w-48 shrink-0 grow-0 text-sm font-normal dark:text-white">
                    {gTab?.name}
                  </div>

                  {gTab.permissions.map((item) => {
                    return (
                      <Fragment key={item.index}>
                        <div className="min-w-12 flex-1">
                          <FormField
                            control={form.control}
                            name={`permissions.${item.index}.value`}
                            render={({ field }) => (
                              <FormCheckbox
                                hideMsg
                                disabled={!isEditable}
                                innerClassName={checkboxStyle}
                                checked={!!field.value}
                                onChange={(e) => {
                                  field.onChange(e.target.checked);
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className="min-w-12 flex-1" />
                        <div className="min-w-12 flex-1" />
                        <div className="min-w-12 flex-1" />
                        <div className="min-w-12 flex-1" />
                      </Fragment>
                    );
                  })}
                </section>
              );
            })}
          </AccordionContent>
        )}
      </AccordionItem>
    </Accordion>
  );
}

function WidgetPermissions({
  form,
  isEditable = true,
}: {
  form: UseFormReturn<UserRoleFormSchema>;
  isEditable?: boolean;
}) {
  const [permissions] = form.watch(['permissions']);

  const { widget } = groupBy(permissions, 'resource_type');

  // console.log(widget);

  return (
    <CardContainer>
      <SectionHeading text={'Dashboard Widgets'} />
      <section className="mt-4 flex flex-col gap-3">
        {widget.map((w, i) => {
          const widgetConfig = dashboardWidgetsPermissionConfig[w.resource as WidgetModelName];

          return (
            <section key={i} className="flex items-end gap-1 gap-x-2">
              <div className="w-7 shrink-0 grow-0 pb-0.5" />
              <div className="text-accent w-60 shrink-0 grow-0 text-left text-sm font-semibold dark:text-white">
                {widgetConfig.name || w.resource}
              </div>

              <div className="min-w-12 flex-1">
                <FormField
                  control={form.control}
                  name={`permissions.${w.index}.value`}
                  render={({ field }) => (
                    <FormCheckbox
                      hideMsg
                      disabled={!isEditable}
                      innerClassName={checkboxStyle}
                      checked={!!field.value}
                      label={w.action}
                      labelClassName={twMerge(
                        'text-c-card-muted text-sm font-semibold capitalize',
                        i === 0 ? '' : 'hidden'
                      )}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  )}
                />
              </div>
              <div className="min-w-12 flex-1" />
              <div className="min-w-12 flex-1" />
              <div className="min-w-12 flex-1" />
              <div className="min-w-12 flex-1" />
            </section>
          );
        })}
      </section>
    </CardContainer>
  );
}
